import type { PortfolioFreelanceProjectItem } from '../../firebase/types/portfolio.types';
import { lines } from './model';
import { CoverImage, LiveLink, ResultPill, UnavailableNotice } from './projectBits';
import { Band, SectionIndex } from './ui';

const NDA_MESSAGE = 'Private client project – details available upon request.';

export default function FreelanceSection({
  projects,
  index,
}: {
  projects: PortfolioFreelanceProjectItem[];
  index: string;
}) {
  return (
    <Band id="freelance">
      <SectionIndex index={index} title="Client work" />
      <div className="grid gap-3 md:grid-cols-2">
        {projects.map((project, i) => {
          const features = lines(project.keyFeatures);
          const liveLabel = project.isNotWorking ? 'Open live site (may be down)' : 'Open live site';
          const title = project.clientName?.trim();
          return (
            <article key={i} className="panel flex h-full flex-col overflow-hidden">
              {project.isNda ? (
                <div className="space-y-4 p-5 md:p-6">
                  <UnavailableNotice active={project.isNotWorking} />
                  <p className="text-base leading-relaxed text-muted">{NDA_MESSAGE}</p>
                </div>
              ) : (
                <>
                  <CoverImage src={project.screenshotUrl} alt={title || project.clientType || 'Client project'} />
                  <div className="flex h-full flex-col p-5 md:p-6">
                    {title ? <h3 className="text-xl font-semibold tracking-tight">{title}</h3> : null}
                    {project.clientType ? (
                      <p className={`${title ? 'mt-1' : ''} text-sm text-signal`}>{project.clientType}</p>
                    ) : null}
                    <div className="mt-3 flex flex-wrap gap-2">
                      <ResultPill value={project.resultHighlight} />
                    </div>
                    <div className="mt-3">
                      <UnavailableNotice active={project.isNotWorking} />
                    </div>
                    <div className="mt-4 space-y-3 text-sm leading-relaxed text-muted md:text-base">
                      {project.problem ? <p><span className="text-ink">Problem. </span>{project.problem}</p> : null}
                      {project.solution ? <p><span className="text-ink">Solution. </span>{project.solution}</p> : null}
                      {project.results ? <p><span className="text-ink">Results. </span>{project.results}</p> : null}
                      {project.techStack ? <p className="text-faint">{project.techStack}</p> : null}
                      {project.timeline ? <p className="text-faint">{project.timeline}</p> : null}
                      {features.length ? (
                        <ul className="space-y-1.5">
                          {features.map((feature, j) => (
                            <li key={j}>{feature}</li>
                          ))}
                        </ul>
                      ) : null}
                    </div>
                    <div className="mt-auto pt-4">
                      <LiveLink href={project.liveLink} label={liveLabel} />
                    </div>
                  </div>
                </>
              )}
            </article>
          );
        })}
      </div>
    </Band>
  );
}
